import { indexByUserId } from "blackswan-api";
import { ActiveConstants, ModelHelper } from "finhelper";
import { Api } from "laravel-request";

interface Account {
    account_number: string;
    groups: Group[];
}

interface PortfolioActiveRow {
    account_number: string;
    type_id: string;
    [key: string]: any;
}

interface Group {
    type_id?: string;
    items: PortfolioActiveRow[];
    [key: string]: any;
}

type AccountsIndex = string[];
type AccountsGroupIndex = string[][];
type DateRange = {
    start: string;
    end: string;
};

const setPayloadParam = (
    payload: Record<string, any>,
    key: string,
    value: string | number | Array<string | number> | null | undefined
) => {
    if (value == null) return;
    payload[key] = value;
};

const getApiBaseUrl = (): string => {
    const explicitUrl = process.env.REACT_APP_API_URL;
    if (explicitUrl) return explicitUrl.replace(/\/+$/, "");
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin.replace(/\/+$/, "");
    }
    return "";
};

/** Laravel / `call` ожидает вложенный query: `data[manager_id]=…`, `data[user_id][]=…`, `data[paginateType]=all`. */
const dataQueryKey = (field: string) => `data[${field}]`;

const appendAggregateQueryParams = (params: URLSearchParams, payload: Record<string, any>) => {
    Object.entries(payload).forEach(([key, value]) => {
        if (value == null) return;
        if (Array.isArray(value)) {
            const arrayKey = `${dataQueryKey(key)}[]`;
            value.forEach((item) => {
                if (item == null) return;
                params.append(arrayKey, String(item));
            });
            return;
        }
        params.append(dataQueryKey(key), String(value));
    });

    if (!Object.prototype.hasOwnProperty.call(payload, "paginateType")) {
        params.append(dataQueryKey("paginateType"), "all");
    }
};

type ValuationApiResponse = { result: string; data: any };

const createDirectApiCall = (action: string, payload: Record<string, any>) => ({
    all: async (
        successCallback: (response: ValuationApiResponse) => void,
        failureCallback?: (error: any) => void
    ) => {
        try {
            const baseUrl = getApiBaseUrl();
            if (!baseUrl) throw new Error("API base URL is not configured");
            const token = typeof localStorage !== "undefined" ? (localStorage.getItem("api_token") || "") : "";
            const params = new URLSearchParams();
            appendAggregateQueryParams(params, payload);
            const url = `${baseUrl}/api/v1/call/aton-daily-valuation-aggregate/${action}?${params.toString()}`;
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: token,
                },
            });
            const json = await response.json();
            if (!response.ok) {
                throw json;
            }
            const normalized = Array.isArray(json) ? { result: "success", data: json } : json;
            successCallback(normalized);
        } catch (error) {
            if (failureCallback) failureCallback(error);
        }
    },
});

const noopPortfolioError = () => {};

export const getSinglePortfolio = (
    id: string | number,
    domain: string | undefined,
    stateCallback: (accounts: Account[]) => void
) =>
    indexByUserId(
        id,
        ({ data }: { data: PortfolioActiveRow[] }): void =>
            succesCallbackOnSinglePortfolio(data, stateCallback),
        domain,
        noopPortfolioError
    );

export const getAverageValuation = (
    id: string | number | null | undefined,
    userId: string | number | (string | number)[] | null | undefined,
    dates?: DateRange,
    accountNumber?: null | number | string | Array<number | string>
) => {
    const payload: Record<string, any> = {};
    setPayloadParam(payload, "manager_id", id);
    setPayloadParam(payload, "user_id", userId);
    setPayloadParam(payload, "account_number", accountNumber);
    if (dates?.start && dates?.end) {
        payload.start = dates.start;
        payload.end = dates.end;
    }
    return createDirectApiCall("sum_valuation", payload);
};

export const getUsersTodayValuation = (
    managerId?: string | number | Array<string | number> | null,
    userId?: string | number | Array<string | number> | null,
    accountNumber?: string | number | Array<string | number> | null
) => {
    const payload: Record<string, any> = {};
    setPayloadParam(payload, "manager_id", managerId);
    setPayloadParam(payload, "user_id", userId);
    setPayloadParam(payload, "account_number", accountNumber);
    return createDirectApiCall("users_today_valuation", payload);
};

export const getUserTodayValuation = (
    userId: string | number,
    managerId?: string | number | Array<string | number> | null
) => {
    const payload: Record<string, any> = {};
    setPayloadParam(payload, "user_id", userId);
    setPayloadParam(payload, "manager_id", managerId);
    return createDirectApiCall("user_today_valuation", payload);
};

export const getUsersMonthlyValuation = (
    date: string,
    managerId?: string | number | Array<string | number> | null,
    userId?: string | number | Array<string | number> | null,
    accountNumber?: string | number | Array<string | number> | null
) => {
    const payload: Record<string, any> = { date };
    setPayloadParam(payload, "manager_id", managerId);
    setPayloadParam(payload, "user_id", userId);
    setPayloadParam(payload, "account_number", accountNumber);
    return createDirectApiCall("users_monthly_valuation", payload);
};

const succesCallbackOnSinglePortfolio = (
    data: PortfolioActiveRow[],
    stateCallback: (accounts: Account[]) => void
) => {
    const accounts: Account[] = [];
    const accuntsIndex: AccountsIndex = [];
    const accuntsGroupIndex: AccountsGroupIndex = [];

    data.forEach((row: { account_number: string; type_id: string }) => {
        let accountIndex = accuntsIndex.indexOf(row.account_number);
        if (accountIndex === -1) accuntsIndex.push(row.account_number);
        accountIndex = accuntsIndex.indexOf(row.account_number);

        if (typeof accounts[accountIndex] === "undefined") {
            accounts[accountIndex] = {
                account_number: row.account_number,
                groups: [],
            };
            accuntsGroupIndex[accountIndex] = [];
        }

        const groupString = `${row.account_number}-${row.type_id}`;
        let groupIndex = accuntsGroupIndex[accountIndex].indexOf(groupString);
        if (groupIndex === -1) {
            accuntsGroupIndex[accountIndex].push(groupString);
        }
        groupIndex = accuntsGroupIndex[accountIndex].indexOf(groupString);

        if (typeof accounts[accountIndex].groups[groupIndex] === "undefined") {
            const typeObject = ActiveConstants.getTypeById(row.type_id);
            accounts[accountIndex].groups[groupIndex] = typeObject ?? {};
            accounts[accountIndex].groups[groupIndex].items = [];
        }
        accounts[accountIndex].groups[groupIndex].items.push(row);
    });

    stateCallback(accounts);
};

export const getSingleUserAccount = (
    userId: number | string,
    callbackOnSuccess: (response: { data: any; result: string; meta?: Record<string, any> }) => void,
    callbackOnFailure: (xhr: XMLHttpRequest) => void
) => {
    Api.get("user-account", "index", { user_id: userId })
        .where("is_visible", 1)
        .with("accounts", "accounts.currency")
        .orderBy("type_id")
        .all(
            (successfulResponse) => {
                ModelHelper.accounts = successfulResponse.data;
                callbackOnSuccess && callbackOnSuccess(successfulResponse);
            },
            (failedResponse) => {
                callbackOnFailure && callbackOnFailure(failedResponse);
            }
        );
};
